/**
 * Editors for one value of a corrected answer, each the way the value reads:
 * text as text (real line breaks; long text grows), true / false as two
 * buttons, a number as a number, anything nested as its own JSON.
 */

import { useState } from "react";

const grow = "inp field-sizing-content max-h-72 min-h-[38px]!";

/** Long or multi-line text gets a growing box. Decided by the original, so the box never swaps mid-typing. */
const isLong = (v: unknown) => typeof v === "string" && (v.length > 60 || v.includes("\n"));

function TextBox({
  id,
  value,
  long,
  onChange,
}: {
  id: string;
  value: string;
  long: boolean;
  onChange: (v: string) => void;
}) {
  return long ? (
    <textarea
      id={id}
      className={`${grow} text-[13px] leading-relaxed`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  ) : (
    <input id={id} className="inp" value={value} onChange={(e) => onChange(e.target.value)} />
  );
}

/** A value as its own JSON; pushed up only while it parses. Empty is `null`. */
export function JsonBox({
  id,
  value,
  onChange,
  placeholder,
}: {
  id: string;
  value: unknown;
  onChange: (v: unknown) => void;
  placeholder?: string;
}) {
  const [text, setText] = useState(() => (value == null ? "" : JSON.stringify(value, null, 2)));
  const [bad, setBad] = useState(false);
  return (
    <div className="flex flex-col gap-1">
      <textarea
        id={id}
        className={`${grow} font-mono text-xs`}
        aria-invalid={bad}
        value={text}
        placeholder={placeholder}
        onChange={(e) => {
          setText(e.target.value);
          try {
            onChange(e.target.value.trim() ? JSON.parse(e.target.value) : null);
            setBad(false);
          } catch {
            setBad(true);
          }
        }}
      />
      {bad && <span className="text-xs text-err">Not valid JSON yet — the last valid value is kept.</span>}
    </div>
  );
}

function YesNo({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="inline-flex self-start rounded-lg border border-input p-0.5">
      {[true, false].map((b) => (
        <button
          key={String(b)}
          type="button"
          aria-pressed={value === b}
          onClick={() => onChange(b)}
          className={`rounded-md px-3 py-1 font-mono text-[13px] ${value === b ? "bg-primary text-on-primary" : "text-body hover:bg-hover"}`}
        >
          {String(b)}
        </button>
      ))}
    </div>
  );
}

export function ValueEditor({
  id,
  value,
  original,
  onChange,
}: {
  id: string;
  value: unknown;
  original: unknown;
  onChange: (v: unknown) => void;
}) {
  if (typeof value === "string")
    return <TextBox id={id} value={value} long={isLong(original)} onChange={onChange} />;
  if (typeof value === "boolean") return <YesNo value={value} onChange={onChange} />;
  if (typeof value === "number") {
    return (
      <input
        id={id}
        type="number"
        className="inp w-40"
        value={value}
        onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
      />
    );
  }
  return <JsonBox id={id} value={value} onChange={onChange} />;
}
